import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { ContentDraft, ContentDraftWithMarkdown, DraftStatus } from '../models/content-draft.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ContentDraftService implements OnDestroy {
  private supabase = inject(SupabaseService);

  private _drafts = signal<ContentDraft[]>([]);
  private _loading = signal(false);
  private _realtimeSub: any;

  drafts = this._drafts.asReadonly();
  loading = this._loading.asReadonly();

  pendingCount = computed(() =>
    this._drafts().filter(d => d.status === 'pending' || d.status === 'edited').length
  );

  constructor() {
    this.loadDrafts('pending', 'edited');
    this._realtimeSub = this.supabase.subscribeToContentDrafts(() => {
      this.loadDrafts('pending', 'edited');
    });
  }

  ngOnDestroy() {
    this._realtimeSub?.unsubscribe();
  }

  async loadDrafts(...statuses: DraftStatus[]) {
    this._loading.set(true);
    const data = statuses.length
      ? await this.supabase.loadContentDrafts(statuses)
      : await this.supabase.loadContentDrafts();
    this._drafts.set(data);
    this._loading.set(false);
  }

  async loadAllDrafts() {
    this._loading.set(true);
    const data = await this.supabase.loadContentDrafts();
    this._drafts.set(data);
    this._loading.set(false);
  }

  async getDraftWithMarkdownBySlug(slug: string): Promise<ContentDraftWithMarkdown | null> {
    const draft = await this.supabase.getContentDraftBySlug(slug);
    return draft ? this._attachMarkdown(draft) : null;
  }

  async getDraftWithMarkdown(id: string): Promise<ContentDraftWithMarkdown | null> {
    const draft = await this.supabase.getContentDraft(id);
    return draft ? this._attachMarkdown(draft) : null;
  }

  private async _attachMarkdown(draft: ContentDraft): Promise<ContentDraftWithMarkdown> {
    const r2Key = draft.r2_draft_key || draft.r2_published_key || draft.r2_rejected_key;
    if (!r2Key || !(environment as any).r2ContentPublicUrl) {
      return { ...draft, markdownContent: '' };
    }
    try {
      const res = await fetch(`${(environment as any).r2ContentPublicUrl}/${r2Key}`);
      const markdownContent = res.ok ? await res.text() : '';
      return { ...draft, markdownContent };
    } catch {
      return { ...draft, markdownContent: '' };
    }
  }

  async saveEdits(id: string, patch: {
    title?: string;
    seo_title?: string;
    seo_description?: string;
    tags?: string[];
    category?: string;
  }): Promise<void> {
    await this.supabase.updateContentDraft(id, {
      ...patch,
      is_edited: true,
      status: 'edited',
    });
    await this.loadDrafts('pending', 'edited');
  }
}
